import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Clock, Pencil, Plus, Trash2 } from "lucide-react";

import { formatTime12 } from "../../lib/mealTypes";

/**
 * One meal slot on a day tab. Renders either the planned meal — timed header,
 * per-item lines with vendor and subtotal, meal total, notes — or an empty
 * state with an Add button.
 *
 * menu_items accepts both shapes for backward compatibility: older meals store
 * plain strings, newer ones store objects with name/vendor_name/price/quantity.
 * Do not drop the string branch; existing MongoDB documents still use it.
 */
export function MealCard({
  mealType,
  Icon,
  color,
  meal,
  mealCost,
  dayNumber,
  canEdit,
  canDelete,
  getVendorName,
  getVendorItemPrice,
  onEdit,
  onDelete,
  onAdd,
}) {
  return (
      <Card key={mealType} className={`border-border/50 shadow-sm transition-all duration-200 ${meal ? "hover:shadow-md" : "opacity-60"}`} data-testid={`meal-card-${dayNumber}-${mealType.replace(/\s/g, "-").toLowerCase()}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${color}`}><Icon className="h-4 w-4" /></div>
              <div>
                <CardTitle className="text-sm font-medium">{mealType}</CardTitle>
                {meal && (meal.start_time || meal.end_time) && (
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                    <Clock className="h-2.5 w-2.5" aria-hidden="true" />
                    {meal.start_time && formatTime12(meal.start_time)}
                    {meal.start_time && meal.end_time && " — "}
                    {meal.end_time && formatTime12(meal.end_time)}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {meal && canEdit && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground print:hidden" onClick={() => { onEdit(meal); }} data-testid={`edit-meal-${meal.meal_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {meal && canDelete && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive print:hidden" onClick={() => onDelete(meal.meal_id)} data-testid={`delete-meal-${meal.meal_id}`}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {meal ? (
            <div className="space-y-2">
              {/* Food Items List */}
              {meal.menu_items?.length > 0 && (
                <div className="divide-y divide-border/50 rounded-md border border-border/40 overflow-hidden" data-testid={`meal-items-${meal.meal_id}`}>
                  {meal.menu_items.map((item, i) => {
                    const isDict = typeof item === "object";
                    const name = isDict ? item.name : item;
                    const vendorName = isDict ? item.vendor_name : getVendorName(meal.vendor_id);
                    const price = isDict ? Number(item.price) : (getVendorItemPrice(meal.vendor_id, item) || 0);
                    const qty = isDict ? (Number(item.quantity) || 1) : 1;
                    const subtotal = price * qty;
                    return (
                      <div key={i} className="flex items-center justify-between px-2.5 py-1.5 text-xs hover:bg-muted/30">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{name}</p>
                          {vendorName && <p className="text-[9px] text-muted-foreground truncate">{vendorName}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 text-right">
                          {qty > 1 && <span className="text-muted-foreground">x{qty}</span>}
                          {subtotal > 0 && <span className="font-semibold text-emerald-600 w-14 text-right">${subtotal.toFixed(2)}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Meal Total */}
              {mealCost > 0 && (
                <div className="flex items-center justify-between pt-1 px-1">
                  <span className="text-[10px] text-muted-foreground font-medium">Meal Total</span>
                  <span className="text-xs font-bold text-emerald-600" data-testid={`meal-total-${meal.meal_id}`}>${mealCost.toFixed(2)}</span>
                </div>
              )}
              {/* Notes */}
              {meal.notes && (
                <p className="text-[10px] text-muted-foreground italic bg-muted/30 rounded px-2 py-1">{meal.notes}</p>
              )}
            </div>
          ) : canEdit ? (
            <Button variant="ghost" size="sm" className="w-full text-muted-foreground print:hidden" onClick={() => { onAdd(mealType); }} data-testid={`add-meal-slot-${dayNumber}-${mealType.replace(/\s/g, "-").toLowerCase()}`}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-2">Not assigned</p>
          )}
        </CardContent>
      </Card>
  );
}

export default MealCard;
